<template>
    <modal-controller-base contentClass="light-light-background">
        <template #header>
            <custom-header back-button :title="$t('titleRoute.productDetails')">
                <template #start>
                    <ion-button @click="modalController.dismiss()" class="purple-light-color">
                        <ion-icon :icon="chevronBack"/>
                        {{ $t('button.back') }}
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <catalog-product
            contained
            :product="product"
            @back="modalController.dismiss()"
            @addList="addToList"
            @addCart="checkOverrideCartProduct"
            @amount="amount = $event; patchItem(listItemId)"
            @targetAmount="targetAmount = $event; patchItem(listItemId)"
            @delete="removeFromList"
            @tags="openTags"
            @openCatalog="openProduct({ product, supplierId, listId, offset: 80, catalog: true })"
            @edit-item="editItem"
            :amount="amount"
            :targetStock="targetAmount"
            :catalog="catalog"
            :show-tags="showTags"
            :tags="listStore.productTags(listId, listItemId)"
            :buttons="buttons"
            :private-supplier="privateSupplier"
        />
    </modal-controller-base>
</template>

<script setup lang="ts">
import {
    actionSheetController,
    ActionSheetOptions,
    IonButton,
    IonIcon,
    modalController
} from "@ionic/vue";
import { ref, computed, nextTick } from "vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";

import CatalogProduct from "@/components/modal/CatalogProduct.vue";
import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import EditTag from "@/components/modal/EditTag.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";

import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import { usePersistentStore } from "@/stores/persistent";

import { interactionFeedbackToast } from "@/functions/baseToast";
import { popAlertConfirm, popAlertOk } from "@/functions/alert";
import handleOfflineAction from "@/functions/offlineMode";
import { openProduct } from "@/functions/modal";

import { List, ListItem } from "@/types/supplier";
import { GetListItemsParams } from "@/types/list";
import { OFFLINE_ACTION } from "@/types/enum/general";
import { SORT_METHOD } from "@/types/enum/propEnum";
import { DashboardCatalogProductProps } from "@/types/props";

import { chevronBack } from "ionicons/icons";
import CreateProduct from "@/components/modal/CreateProduct.vue";

const props = defineProps<DashboardCatalogProductProps>()

const supplierStore = useSupplierStore()
const listStore = useListStore()
const persistentStore = usePersistentStore()

const amount = ref<number>(props.amount ?? 0)
const targetAmount = ref<number>(props.targetStock ?? 0)
const saveTimer = ref<number | null>(null)

const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))

const patchItem = async (itemId: string, noReload?: boolean): Promise<void> => {
    if (props.catalog) return
    if (persistentStore.offlineMode && props.listId) {
        if (!noReload) listStore.isLoaded.listItems = false
        const item = listStore.listItems[props.listId].data?.find((item) => item.id === itemId)
        if (item) {
            item.quantity = amount.value
            item.targetStock = targetAmount.value
        }
        if (!noReload) nextTick(() => {
            listStore.isLoaded.listItems = true
        })

        handleOfflineAction(OFFLINE_ACTION.PATCH_ITEM, {
            locationId: locationId.value,
            listId: props.listId,
            itemId: itemId,
            amount: amount.value,
            targetStock: targetAmount.value
        })
        return
    }
    if (saveTimer.value) clearTimeout(saveTimer.value)

    saveTimer.value = setTimeout(async () => {
        if (props.listId) {
            const item = listStore.listItems[props.listId].data?.find((item) => item.id === itemId)
            if (item) {
                item.quantity = amount.value
                item.targetStock = targetAmount.value
            }
            window.dispatchEvent(new Event('force-reload-catalog'))
        }
        await listStore.patchListItem(locationId.value, props.listId ?? '', itemId, amount.value, targetAmount.value)
    }, 500)
}

const removeFromList = async (): Promise<void> => {
    await popAlertConfirm({
        message: t('alert.delete.product', { target: props.product.name }),
        handler: {
            ok: () => deleteHandler(props.listItemId ?? '', props.listId ?? '')
        }
    })
}

const reloadListItems = async (listId: string): Promise<void> => {
    listStore.isLoaded.listItems = false

    await listStore.getListItems(new GetListItemsParams({
        locationId: locationId.value,
        listId,
        searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
        category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
        tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
        withQuantity: listStore.listFilter.withQuantity,
        sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC),
        sortField: (supplierStore.sortDesc) ? 'name' : undefined,
        take: 0
    }))

    listStore.isLoaded.listItems = true
}

const deleteHandler = async (itemId: string, listId: string): Promise<void> => {
    listStore.isLoaded.listItems = false

    if (persistentStore.offlineMode) {
        const itemIndex = listStore.listItems[listId].data?.findIndex((item) => item.id === itemId)

        if (itemIndex >= 0) {
            listStore.listItems[listId].data?.splice(itemIndex, 1)
            listStore.listItems[listId].count -= 1
        }
        await modalController.dismiss()
        await handleOfflineAction(OFFLINE_ACTION.DELETE_ITEM, {
            locationId: locationId.value,
            listId: listId,
            itemId: itemId,
        })
        return
    }

    const response = await listStore.deleteItem(locationId.value, listId, itemId)

    if (response) {
        interactionFeedbackToast(
            response,
            {
                id: 'delete-list',
                type: 'delete',
                target: 'product',
            },
            {
                cssClass: 'toast-offset-80'
            }
        )
        reloadListItems(listId)
        await modalController.dismiss()
    }
}

const addItem = async (listId: string): Promise<void> => {
    if (persistentStore.offlineMode) {
        const itemId = 'new-' + new Date().getTime().toString()
        if (!listStore.listItems[listId].data?.find(item => item.item.id === props.product.id)) {
            const newListItem: ListItem = {
                id: itemId,
                item: props.product,
                listId: listId,
                quantity: amount.value,
                supplierItemId: props.product.id,
                targetStock: targetAmount.value,
                piecePrice: 0
            }
            listStore.listItems[listId].data?.push(newListItem)
        } else {
            interactionFeedbackToast(false, {
                type: 'returnType.ItemAlreadyInList',
                id: 'add-product-list'
            }, {
                cssClass: `toast-offset-70`
            }, true)
            return
        }
        await modalController.dismiss()
        handleOfflineAction(OFFLINE_ACTION.ADD_ITEM, {
            locationId: locationId.value,
            listId: listId,
            itemId: itemId,
            amount: amount.value,
            supplierItemId: props.product.id,
            targetStock: targetAmount.value
        })
        return
    }

    modalController.dismiss()

    const response = await listStore.addListItem(locationId.value, listId, props.product.id, amount.value, targetAmount.value)

    if (response) {
        listStore.isLoaded.listItems = false

        await interactionFeedbackToast(response, {
            type: 'addList',
            id: 'add-product-list'
        }, {
            cssClass: `toast-offset-${props.offset ?? 70}`
        }, true)

        await listStore.getListItems(new GetListItemsParams({
            locationId: locationId.value,
            listId,
            searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
            category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC),
            take: 0
        }))

        const list = listStore.singleList(listId)

        if (list) supplierStore.setInList(list, props.product.id)

        listStore.isLoaded.listItems = true
    }
}

const addToList = async (): Promise<void> => {
    if (props.listId && props.catalog) return await addItem(props.listId)

    const listList = listStore.lists.data.filter((list: List) => list.supplierId === props.supplierId)

    if (listList.length === 0) return await popAlertOk({
        message: t('alert.noList')
    })

    if (listList.length === 1) {
        const list = listList[0]
        if (!list?.id) return
        return addItem(list.id)
    }

    const params: ActionSheetOptions = {
        header: t('alert.selectList'),
        buttons: [],
        cssClass: 'custom-action-sheet',
        mode: 'ios'
    }

    for (const list of listList) {
        if (list.id) params.buttons.push({
            text: list.name,
            role: 'select',
            handler: () => {
                if (!list.id) return false
                addItem(list.id)
            }
        })
    }

    params.buttons.push({
        text: t('button.cancel'),
        role: 'cancel',
        data: {
            action: 'cancel'
        }
    })

    const actionSheet = await actionSheetController.create(params);
    actionSheet.onDidDismiss()
    await actionSheet.present();
}

const addToCart = (): void => {
    modalController.dismiss()

    listStore.addItemToCart(locationId.value, props.product.id, amount.value, props.supplierId).then((response) => {
        if (response) {
            listStore.getShoppingCart(locationId.value)
            interactionFeedbackToast(
                response,
                {
                    type: 'addCart',
                    id: 'add-product-cart'
                },
                {
                    cssClass: `toast-offset-${props.offset ?? 50}`
                },
                true
            )
        }
    })
}

const checkOverrideCartProduct = async (): Promise<void> => {
    if (listStore.catalogItemInCart(props.product.id)) {
        popAlertConfirm({
            message: t('messages.list.overrideProductList'),
            cancelText: t('button.cancel'),
            handler: {
                ok: addToCart
            }
        })
        return
    }

    addToCart()
}

const openTags = async (): Promise<void> => {
    const modal = await modalController.create({
        component: EditTag,
        cssClass: 'scrollable-at-max-breakpoint',
        componentProps: {
            listId: props.listId,
            listItemId: props.listItemId
        },
        breakpoints: [0, 0.3, 0.5, 0.7, 0.95],
        initialBreakpoint: 0.5
    })

    listStore.getTags(locationId.value)

    await modal.present()
}

const editItem = async () => {
    await (await modalController.create({
        component: CreateProduct,
        componentProps: {
            supplierId: supplierStore.selectedSupplierId,
            listId: route.value.params.listId as string,
            listItemId: props.listItemId,
            product: props.product,
            edit: true
        }
    })).present()
}
</script>
